import { useState } from 'react';
import { Box, Tooltip, useMediaQuery, Link, Typography, type Theme } from '@mui/material';
import { Badge } from '@care/react-component-lib';
import { Icon24InfoBgcFilled, Icon24InfoPremiumFilled } from '@care/react-icons';
import {
  getSearchResults_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess_caregiverSearchResults as SEOSegmentationCaregiverSearchResult,
  getSearchResults_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess_caregiverSearchResults_caregiver as Caregiver,
} from '@/__generated__/getSearchResults';

export type BadgeState = {
  PREMIUM: boolean;
  BACKGROUND_CHECK: boolean;
};

interface RobustCaregiverInfoProps {
  caregiver: Caregiver;
  url: SEOSegmentationCaregiverSearchResult['profileURL'];
}

const RobustCaregiverInfo = ({ caregiver, url }: RobustCaregiverInfoProps) => {
  const isMobileOrSmaller = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const targetValue = isMobileOrSmaller ? '' : '_blank';
  const {
    member: { displayName, address, isPremium },
    hasCareCheck,
  } = caregiver;

  const [tooltipsOpen, setTooltipsOpen] = useState<BadgeState>({
    PREMIUM: false,
    BACKGROUND_CHECK: false,
  });
  const handleTooltipOpen = (badgeType: 'PREMIUM' | 'BACKGROUND_CHECK') => {
    setTooltipsOpen({ ...tooltipsOpen, [badgeType]: !tooltipsOpen[badgeType] });
  };

  const generateTooltip = (
    title: string,
    icon: React.ReactNode,
    badgeType: 'PREMIUM' | 'BACKGROUND_CHECK',
    tooltipHandler: (badgeType: 'PREMIUM' | 'BACKGROUND_CHECK') => void
  ) => {
    return (
      <Tooltip
        PopperProps={{
          disablePortal: true,
        }}
        title={title}
        placement="bottom"
        arrow>
        <Badge
          data-testid={`${badgeType.toLowerCase()}-badge`}
          onMouseOver={() => tooltipHandler(badgeType)}
          icon={icon}
          sx={{
            ml: 0.5,
            mr: -1,
            cursor: 'pointer',
            height: '100%',
            position: 'relative',
            top: '3px',
          }}
        />
      </Tooltip>
    );
  };
  if (url === null) return null;
  return (
    <Link variant="link1" underline="none" href={url} target={targetValue}>
      <Typography variant="h2">{displayName}</Typography>
      {hasCareCheck && (
        <span>
          <Box display="inline-flex" alignItems="center">
            {generateTooltip(
              'Background check - all caregivers must complete an initial screening in order to interact with families.',
              <Icon24InfoBgcFilled />,
              'BACKGROUND_CHECK',
              handleTooltipOpen
            )}
          </Box>
        </span>
      )}
      {isPremium && (
        <span>
          <Box display="inline-flex" alignItems="center">
            {generateTooltip(
              'Premium Care.com member',
              <Icon24InfoPremiumFilled />,
              'PREMIUM',
              handleTooltipOpen
            )}
          </Box>
        </span>
      )}
      <br />
      {address?.city && address?.state && (
        <Typography component="span" variant="body1">
          {address.city}, {address.state}
        </Typography>
      )}
    </Link>
  );
};

export default RobustCaregiverInfo;
